import { useMemo, useState } from "react";
import { Inbox } from "lucide-react";
import { PageShell } from "./page-shell";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { EmptyState, ErrorState } from "@/components/common/empty-state";
import { StatusBadge } from "@/components/common/status-badge";
import { useAssignHandoff, useHandoffs, useUpdateHandoff } from "@/lib/queries";
import { formatRelative } from "@/lib/utils";
import { HandoffDrawer } from "./handoff-drawer";
import { useToast } from "@/components/ui/toast";
import type { Handoff } from "@/lib/types";

type Filter = "open" | "in_progress" | "resolved" | "all";

const FILTER_LABEL: Record<Filter, string> = {
  open: "Open",
  in_progress: "In progress",
  resolved: "Resolved",
  all: "All",
};

export function HandoffsPage() {
  const { data, isLoading, isError, refetch } = useHandoffs();
  const update = useUpdateHandoff();
  const assign = useAssignHandoff();
  const toast = useToast();
  const [filter, setFilter] = useState<Filter>("open");
  const [selected, setSelected] = useState<Handoff | null>(null);

  const filtered = useMemo(() => {
    if (!Array.isArray(data)) return [];
    if (filter === "all") return data;
    return data.filter((h) => {
      const s = (h.status || "").toLowerCase();
      if (filter === "open") return s === "open" || s === "pending" || !s;
      if (filter === "in_progress") return s === "in_progress" || s === "assigned";
      if (filter === "resolved") return s === "resolved" || s === "closed";
      return true;
    });
  }, [data, filter]);

  const counts = useMemo(() => {
    const list = Array.isArray(data) ? data : [];
    const base: Record<Filter, number> = { open: 0, in_progress: 0, resolved: 0, all: list.length };
    for (const h of list) {
      const s = (h.status || "").toLowerCase();
      if (s === "open" || s === "pending" || !s) base.open += 1;
      else if (s === "in_progress" || s === "assigned") base.in_progress += 1;
      else if (s === "resolved" || s === "closed") base.resolved += 1;
    }
    return base;
  }, [data]);

  const onResolve = async (h: Handoff) => {
    try {
      await update.mutateAsync({ id: h.id, body: { status: "resolved" } });
      toast.success("Handoff resolved");
    } catch (err) {
      toast.error("Resolve failed", err instanceof Error ? err.message : undefined);
    }
  };

  const onAssign = async (h: Handoff) => {
    try {
      await assign.mutateAsync(h.id);
      toast.success("Assigned to you");
    } catch (err) {
      toast.error("Assign failed", err instanceof Error ? err.message : undefined);
    }
  };

  return (
    <>
      <PageShell
        title="Handoffs"
        description="Conversations where the bot asked a human for help. Triage in order of arrival."
      >
        <div className="flex flex-wrap items-center gap-1">
          {(Object.keys(FILTER_LABEL) as Filter[]).map((key) => (
            <Button
              key={key}
              variant={filter === key ? "outline" : "ghost"}
              size="sm"
              onClick={() => setFilter(key)}
              className={filter === key ? "bg-surface" : ""}
            >
              {FILTER_LABEL[key]}
              <span className="text-xs text-ink-faint">({counts[key]})</span>
            </Button>
          ))}
        </div>

        <div className="rounded-lg border border-line bg-surface">
          {isLoading ? (
            <div className="space-y-2 p-4">
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} className="h-9 w-full" />
              ))}
            </div>
          ) : isError ? (
            <ErrorState
              title="Couldn't load handoffs"
              description="Check your connection or retry."
              onRetry={() => void refetch()}
            />
          ) : filtered.length === 0 ? (
            <EmptyState
              icon={Inbox}
              title={filter === "open" ? "Nothing waiting" : `No ${FILTER_LABEL[filter].toLowerCase()} handoffs`}
              description={
                filter === "open"
                  ? "The bot hasn't escalated anything in this category."
                  : "Try a different filter to see other items."
              }
            />
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Lead</TableHead>
                  <TableHead>Reason</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Opened</TableHead>
                  <TableHead className="w-1"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((h) => (
                  <TableRow key={h.id} className="cursor-pointer" onClick={() => setSelected(h)}>
                    <TableCell>
                      <div className="font-medium text-ink">{h.full_name || "Unnamed lead"}</div>
                      {h.phone && (
                        <div className="font-mono text-xs text-ink-faint">{h.phone}</div>
                      )}
                    </TableCell>
                    <TableCell className="max-w-md">
                      <div className="truncate text-ink">{h.reason || "—"}</div>
                      {h.summary && (
                        <div className="truncate text-xs text-ink-muted">{h.summary}</div>
                      )}
                    </TableCell>
                    <TableCell>
                      <StatusBadge status={h.status || "open"} />
                    </TableCell>
                    <TableCell className="text-sm text-ink-muted">{formatRelative(h.created_at)}</TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center justify-end gap-1">
                        {(h.status === "open" || !h.status) && (
                          <Button variant="ghost" size="sm" onClick={() => onAssign(h)}>
                            Assign
                          </Button>
                        )}
                        {h.status !== "resolved" && (
                          <Button variant="ghost" size="sm" onClick={() => onResolve(h)}>
                            Resolve
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      </PageShell>

      <HandoffDrawer handoff={selected} onClose={() => setSelected(null)} />
    </>
  );
}
